import { execSync } from 'child_process';
import fs from 'fs';
import JSZip from 'jszip';
import * as path from 'path';

interface CommandInformation {
  id: string;
  type: string;
  workspace: string;
  command: string;
  status: string;
  out: string;
}

export function createWorkspaceContext(
  readSettings: Function,
  commandInfos: CommandInformation[],
  workspace: string
): Promise<string> {
  const relevant = commandInfos.filter(c => c.workspace === workspace);
  const workspacePath = readSettings().recent.find(
    (w: any) => w.name === workspace
  ).path;
  const zip = new JSZip();
  addLogs(zip, relevant, workspacePath);
  addConfigFiles(zip, workspacePath);
  addFileChanges(zip, workspacePath);
  return zip.generateAsync({ type: 'base64' });
}

function addConfigFiles(zip: any, workspacePath: string) {
  zip.file('package.json', readFile(workspacePath, 'package.json'));
  zip.file('angular.json', readFile(workspacePath, 'angular.json'));
  zip.file('tslint.json', readFile(workspacePath, 'tslint.json'));
  zip.file('tsconfig.json', readFile(workspacePath, 'tsconfig.json'));
}

function addLogs(
  zip: any,
  relevant: CommandInformation[],
  workspacePath: string
) {
  const logs = zip.folder('logs');
  relevant.forEach((e, index) => {
    try {
      logs.file(
        toFileName(e, index),
        `
# id: ${e.id}

# type: ${e.type}
    
# workspace: ${e.workspace}

# workspacePath: ${workspacePath}

# command: ${e.command}

# status: ${e.status}
    

${e.out}
    `
      );
    } catch (e) {
      console.log(e.message);
    }
  });
}

function toFileName(e: { command: string }, index: number): string {
  return `${index}-${e.command.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
}

function readFile(workspacePath: string, fileName: string) {
  try {
    return fs.readFileSync(path.join(workspacePath, fileName)).toString();
  } catch (e) {
    console.log(e.message);
    return 'cannot find file';
  }
}

function addFileChanges(zip: any, workspacePath: string) {
  try {
    const fileChanges = `
---------------------------------------------    
getChangesUsingBaseAndHead:  

${getChangesUsingBaseAndHead('master', 'HEAD', workspacePath)}  
---------------------------------------------


---------------------------------------------
getUncommitedChanges:

${getUncommitedChanges(workspacePath)}
---------------------------------------------


---------------------------------------------
getUntrackedFiles:

${getUntrackedFiles(workspacePath)}
---------------------------------------------
  `;
    zip.file('filechanges.txt', fileChanges);
  } catch (e) {
    console.log('Error in addFileChanges', e.message);
  }
}

function getUncommitedChanges(workspacePath: string): string {
  return executeGitCommand(`git diff HEAD .`, workspacePath);
}

function getUntrackedFiles(workspacePath: string): string {
  const fileNames = executeGitCommand(
    `git ls-files --others --exclude-standard`,
    workspacePath
  )
    .split('\n')
    .map(a => a.trim())
    .filter(a => a.length > 0);

  if (fileNames.length > 10) {
    return 'too many untracked files';
  }

  return fileNames
    .map(file => {
      try {
        const stats = fs.statSync(path.join(workspacePath, file));
        const fileSizeInBytes = stats.size;
        if (fileSizeInBytes > 20000) {
          return `filename:${file}\n\nCannot show content. The file is greater than 20k.`;
        } else {
          const content = fs
            .readFileSync(path.join(workspacePath, file))
            .toString();
          return `filename:${file}\n\n${content}`;
        }
      } catch (e) {
        return `filename:${file}\n\nexception:${e.message}`;
      }
    })
    .join(`\n\n`);
}

function getChangesUsingBaseAndHead(
  base: string,
  head: string,
  workspacePath: string
): string {
  const mergeBase = execSync(`git merge-base ${base} ${head}`, {
    cwd: workspacePath
  })
    .toString()
    .trim();
  return executeGitCommand(`git diff ${mergeBase} ${head}`, workspacePath);
}

function executeGitCommand(command: string, workspacePath: string): string {
  return execSync(command, { cwd: workspacePath }).toString('utf-8');
}
